import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, Mail, User, Eye, EyeOff } from "lucide-react";
import { AlertMessage } from "@/components/ui/alert-message";
import { AuthLayout } from "@/components/layout/AuthLayout";

export default function SignUpPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const { signUp, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Present when the user arrived via an organization invitation link — after
  // email verification they are sent back to the accept page.
  const invitationId = searchParams.get("invitation");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password || !passwordConfirm || !name) {
      setError("All fields are required");
      return;
    }

    if (password !== passwordConfirm) {
      setError("Passwords do not match");
      return;
    }

    try {
      // Store the current values before sign-up attempt
      const currentEmail = email;
      const currentPassword = password;
      const currentName = name;

      const response = await signUp(currentEmail, currentPassword, currentName, {
        callbackURL: invitationId
          ? `${window.location.origin}/accept-invitation/${invitationId}`
          : undefined,
      });

      // Check if user email is verified
      if (
        response &&
        response.data?.user &&
        response.data.user.emailVerified === false
      ) {
        // Redirect to verification pending page if email is not verified
        navigate("/verification-pending", { replace: true });
      } else {
        // Redirect to the dashboard if email is verified or verification not required
        navigate("/", { replace: true });
      }
    } catch (err: any) {
      // On error, keep the form values and show the error message
      console.error("Error during sign up:", err);
      setError(
        err.message || "Failed to sign up. Please check your information."
      );
    }
  };

  return (
    <AuthLayout
      title="Create Account"
      subtitle="Sign up to get started with Youzu"
      leftContent={{
        title: "Join Youzu",
        subtitle: "& Get Started",
        description:
          "Transform your digital experience with our powerful AI-powered visual commerce tools. Start analyzing performance and boosting conversions today.",
        trustIndicator: "Free to get started",
      }}
    >
      <AlertMessage message={error} type="error" />
      {invitationId && (
        <AlertMessage
          message="You're joining an organization — sign up with the email address the invitation was sent to."
          type="success"
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <User className="h-4 w-4 text-gray-400" />
            </div>
            <Input
              id="name"
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
              required
              className="pl-10 h-12"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <Input
              id="email"
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              className="pl-10 h-12"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Lock className="h-4 w-4 text-gray-400" />
            </div>
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password (min. 8 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              className="pl-10 pr-10 h-12"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Lock className="h-4 w-4 text-gray-400" />
            </div>
            <Input
              id="passwordConfirm"
              type={showPasswordConfirm ? "text" : "password"}
              placeholder="Confirm Password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              disabled={isLoading}
              required
              className="pl-10 pr-10 h-12"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
              tabIndex={-1}
            >
              {showPasswordConfirm ? (
                <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium rounded-lg transition-colors"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Creating account...
            </>
          ) : (
            "Sign up"
          )}
        </Button>
      </form>

      <div className="mt-6 text-center space-y-2">
        <p className="text-sm text-gray-600">
          Already have an account?{" "}
          <Link
            to="/signin"
            className="font-medium text-purple-600 hover:text-purple-500"
          >
            Sign in
          </Link>
        </p>
        <p className="text-xs text-gray-500">
          Need to verify your email?{" "}
          <Link
            to="/resend-verification"
            className="font-medium text-purple-600 hover:text-purple-500"
          >
            Resend verification
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
